import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import SharedCatalogueChrome from "./SharedCatalogueChrome";
import { CatalogueVisitorProvider, useCatalogueVisitor } from "./CatalogueVisitorContext";
import { loadVisitorOrders } from "./visitorSubmittedOrders";
import { VisitorSubmittedOrdersSection } from "./VisitorSubmittedOrdersSection";
import "./sharedCatalogue.css";

/**
 * Dedicated page listing submitted-to-studio snapshots (same data as bottom of visitor account).
 */
const PublicCatalogueVisitorSubmittedInner: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { isVisitorSession, profile, openAuthModal } = useCatalogueVisitor();
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>({});

  const orderLog = useMemo(
    () => (profile?.visitorId ? loadVisitorOrders(profile.visitorId) : []),
    [profile?.visitorId, location.key, location.pathname]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const headerActions = (
    <>
      <button
        type="button"
        className="hkp-shared-linkish"
        onClick={() => history.push("/catalogue-visitor/account")}
      >
        Your selections
      </button>
      {!isVisitorSession ? (
        <button type="button" className="hkp-shared-linkish" onClick={() => openAuthModal()}>
          Sign in
        </button>
      ) : null}
    </>
  );

  return (
    <SharedCatalogueChrome
      organizationName="Submitted to studio"
      subtitle={profile?.name ? `Signed in as ${profile.name}` : "Your sent requests"}
      pillText={isVisitorSession && profile?.name?.trim() ? "" : "Visitor"}
      headerActions={headerActions}
    >
      <div className="hkp-visitor-account">
        <div className="hkp-visitor-account-toolbar">
          <button type="button" className="hkp-shared-back" onClick={() => history.goBack()}>
            ← Back
          </button>
          <button
            type="button"
            className="hkp-visitor-account-text-btn"
            onClick={() => history.push("/catalogue-visitor/account")}
          >
            Edit selections and cart
          </button>
        </div>

        {!isVisitorSession ? (
          <div className="hkp-shared-muted hkp-visitor-account-empty-msg">
            <p>Sign in to see requests you sent from this browser.</p>
            <button type="button" className="hkp-shared-back" onClick={() => openAuthModal()}>
              Sign in
            </button>
          </div>
        ) : orderLog.length === 0 ? (
          <div className="hkp-shared-muted hkp-visitor-account-empty-msg">
            <p>No submissions stored here yet. When you send a cart or shortlist from your visitor account, a copy appears as a condensed invoice on this page.</p>
            <button
              type="button"
              className="hkp-visitor-account-outline-btn"
              onClick={() => history.push("/catalogue-visitor/account")}
            >
              Go to your selections
            </button>
          </div>
        ) : (
          <VisitorSubmittedOrdersSection
            orderLog={orderLog}
            expandedOrders={expandedOrders}
            onToggleOrder={(id) =>
              setExpandedOrders((prev) => ({ ...prev, [id]: !prev[id] }))
            }
            profile={
              profile
                ? { name: profile.name, phone: profile.phone, email: profile.email }
                : null
            }
          />
        )}
      </div>
    </SharedCatalogueChrome>
  );
};

const PublicCatalogueVisitorSubmitted: React.FC = () => (
  <CatalogueVisitorProvider>
    <PublicCatalogueVisitorSubmittedInner />
  </CatalogueVisitorProvider>
);

export default PublicCatalogueVisitorSubmitted;
